/**
 * Pre-made smile arch template definitions.
 * Each template defines a full arch of teeth with proportions and styling.
 */

export const SMILE_TEMPLATES = {
  classicSmile: {
    id: 'classicSmile',
    name: 'Classic Smile',
    description: 'Balanced proportions with moderate tooth display',
    teeth: [
      { type: 'centralIncisor', variant: 'natural', relativeWidth: 1.0, relativeHeight: 1.0 },
      { type: 'lateralIncisor', variant: 'natural', relativeWidth: 0.618, relativeHeight: 0.92 },
      { type: 'canine', variant: 'natural', relativeWidth: 0.382, relativeHeight: 0.96 },
      { type: 'firstPremolar', variant: 'natural', relativeWidth: 0.35, relativeHeight: 0.82 },
      { type: 'secondPremolar', variant: 'natural', relativeWidth: 0.32, relativeHeight: 0.78 },
    ],
    archCurvature: 0.03,
    gingivalVariation: { lateralIncisor: 0.3, canine: -0.15, firstPremolar: 0.2, secondPremolar: 0.3 },
    defaultColor: '#F5F0E8',
    teethPerSide: 5,
  },

  broadSmile: {
    id: 'broadSmile',
    name: 'Broad Smile',
    description: 'Wider arch with more teeth visible, Hollywood-style',
    teeth: [
      { type: 'centralIncisor', variant: 'natural', relativeWidth: 1.0, relativeHeight: 1.0 },
      { type: 'lateralIncisor', variant: 'natural', relativeWidth: 0.65, relativeHeight: 0.93 },
      { type: 'canine', variant: 'natural', relativeWidth: 0.42, relativeHeight: 0.97 },
      { type: 'firstPremolar', variant: 'natural', relativeWidth: 0.38, relativeHeight: 0.85 },
      { type: 'secondPremolar', variant: 'natural', relativeWidth: 0.35, relativeHeight: 0.80 },
    ],
    archCurvature: 0.02,
    gingivalVariation: { lateralIncisor: 0.2, canine: -0.1, firstPremolar: 0.15, secondPremolar: 0.2 },
    defaultColor: '#FAFAFA',
    teethPerSide: 5,
  },

  narrowSmile: {
    id: 'narrowSmile',
    name: 'Narrow Smile',
    description: 'Narrower arch, fewer teeth displayed',
    teeth: [
      { type: 'centralIncisor', variant: 'rounded', relativeWidth: 1.0, relativeHeight: 1.0 },
      { type: 'lateralIncisor', variant: 'rounded', relativeWidth: 0.60, relativeHeight: 0.90 },
      { type: 'canine', variant: 'rounded', relativeWidth: 0.36, relativeHeight: 0.94 },
      { type: 'firstPremolar', variant: 'rounded', relativeWidth: 0.30, relativeHeight: 0.78 },
    ],
    archCurvature: 0.04,
    gingivalVariation: { lateralIncisor: 0.35, canine: -0.2, firstPremolar: 0.25 },
    defaultColor: '#F5F0E8',
    teethPerSide: 4,
  },

  feminineSmile: {
    id: 'feminineSmile',
    name: 'Feminine Smile',
    description: 'Rounded central incisors with softer contours',
    teeth: [
      { type: 'centralIncisor', variant: 'rounded', relativeWidth: 1.0, relativeHeight: 1.0 },
      { type: 'lateralIncisor', variant: 'rounded', relativeWidth: 0.62, relativeHeight: 0.88 },
      { type: 'canine', variant: 'rounded', relativeWidth: 0.40, relativeHeight: 0.92 },
      { type: 'firstPremolar', variant: 'rounded', relativeWidth: 0.36, relativeHeight: 0.80 },
      { type: 'secondPremolar', variant: 'rounded', relativeWidth: 0.33, relativeHeight: 0.76 },
    ],
    archCurvature: 0.035,
    gingivalVariation: { lateralIncisor: 0.3, canine: -0.1, firstPremolar: 0.2, secondPremolar: 0.25 },
    defaultColor: '#F8F4EE',
    teethPerSide: 5,
  },

  masculineSmile: {
    id: 'masculineSmile',
    name: 'Masculine Smile',
    description: 'Squarer central incisors with more angular edges',
    teeth: [
      { type: 'centralIncisor', variant: 'square', relativeWidth: 1.0, relativeHeight: 1.0 },
      { type: 'lateralIncisor', variant: 'square', relativeWidth: 0.60, relativeHeight: 0.90 },
      { type: 'canine', variant: 'square', relativeWidth: 0.40, relativeHeight: 0.95 },
      { type: 'firstPremolar', variant: 'square', relativeWidth: 0.37, relativeHeight: 0.83 },
      { type: 'secondPremolar', variant: 'square', relativeWidth: 0.34, relativeHeight: 0.79 },
    ],
    archCurvature: 0.02,
    gingivalVariation: { lateralIncisor: 0.25, canine: -0.15, firstPremolar: 0.2, secondPremolar: 0.25 },
    defaultColor: '#F0ECE4',
    teethPerSide: 5,
  },

  youthfulSmile: {
    id: 'youthfulSmile',
    name: 'Youthful Smile',
    description: 'Slightly longer centrals with visible mamelons',
    teeth: [
      { type: 'centralIncisor', variant: 'natural', relativeWidth: 1.0, relativeHeight: 1.08 },
      { type: 'lateralIncisor', variant: 'natural', relativeWidth: 0.60, relativeHeight: 0.88 },
      { type: 'canine', variant: 'natural', relativeWidth: 0.38, relativeHeight: 0.95 },
      { type: 'firstPremolar', variant: 'natural', relativeWidth: 0.34, relativeHeight: 0.80 },
      { type: 'secondPremolar', variant: 'natural', relativeWidth: 0.31, relativeHeight: 0.76 },
    ],
    archCurvature: 0.04,
    gingivalVariation: { lateralIncisor: 0.35, canine: -0.1, firstPremolar: 0.25, secondPremolar: 0.3 },
    defaultColor: '#FAFAF5',
    teethPerSide: 5,
  },
};

// Tooth shade presets
export const SHADE_PRESETS = [
  { id: 'BL1', name: 'BL1', color: '#FAFAFA', description: 'Bleach 1 - Brightest' },
  { id: 'A1', name: 'A1', color: '#F5F0E8', description: 'A1 - Light' },
  { id: 'A2', name: 'A2', color: '#F0ECE4', description: 'A2 - Natural' },
  { id: 'A3', name: 'A3', color: '#E8E0D0', description: 'A3 - Medium' },
  { id: 'A35', name: 'A3.5', color: '#DDD5C0', description: 'A3.5 - Darker' },
];

// Design presets (golden proportion-based parameter sets)
export const DESIGN_PRESETS = {
  golden: {
    id: 'golden',
    name: 'Golden Ratio',
    description: 'Classic golden proportion',
    params: {
      centralWidth: 0.15,
      lateralRatio: 0.618,
      canineRatio: 0.382,
      smileArcCurvature: 0.03,
      teethPerSide: 5,
    },
  },
  natural: {
    id: 'natural',
    name: 'Natural',
    description: 'Slightly wider laterals for a natural look',
    params: {
      centralWidth: 0.14,
      lateralRatio: 0.66,
      canineRatio: 0.44,
      smileArcCurvature: 0.025,
      teethPerSide: 5,
    },
  },
  hollywood: {
    id: 'hollywood',
    name: 'Hollywood',
    description: 'Wider centrals with prominent smile arc',
    params: {
      centralWidth: 0.16,
      lateralRatio: 0.618,
      canineRatio: 0.382,
      smileArcCurvature: 0.04,
      teethPerSide: 5,
    },
  },
  conservative: {
    id: 'conservative',
    name: 'Conservative',
    description: 'Subtle proportions with wider laterals',
    params: {
      centralWidth: 0.13,
      lateralRatio: 0.70,
      canineRatio: 0.50,
      smileArcCurvature: 0.02,
      teethPerSide: 4,
    },
  },
};

/**
 * Get all template definitions.
 */
export const getAllTemplates = () => Object.values(SMILE_TEMPLATES);

/**
 * Get a template by ID.
 */
export const getTemplateById = (id) => SMILE_TEMPLATES[id] || null;

/**
 * Get all design presets.
 */
export const getAllPresets = () => Object.values(DESIGN_PRESETS);

/**
 * Get a shade preset by ID.
 */
export const getShadeById = (id) => SHADE_PRESETS.find(s => s.id === id) || null;
